import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { ContentPaste, SyncProblem } from "@mui/icons-material";
import { Box, Typography, useTheme, type SxProps } from "@mui/material";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type PropsWithChildren,
} from "react";
import invariant from "tiny-invariant";
import ContextMenuDropdown, {
  type ContextMenuRef,
} from "../../../../components/shared/ContextMenuDropdown";
import type { DraggedItem } from "../../../../components/shared/dragndrop/types";
import Loading from "../../../../components/shared/layout/Loading";
import { CollapsibleItem } from "../../../../components/shared/sidebar/CollapsibleItem";
import { useSelectionContext } from "../../../../context/SelectionContext";
import { useOSADShipments } from "../../../../hooks/react-query/operations/useOSAD";
import { useDateSearchParamOrFallbackToToday } from "../../../../hooks/useDateSearchParamOrFallbackToToday";
import { useSelectedServiceCenter } from "../../../../hooks/useSelectedServiceCenter";
import { osadDefaultValue } from "../../../../mocks/operations/osad.mock";
import type { ContextMenuAction } from "../../../../types/contextMenuAction";
import { dateToInt } from "../../../../utils/dateTimeHelper";
import { OSADShipmentItem } from "../../inboundPlanning/OSADShipmentItem";
import { osadContextMenuActions } from "../../inboundPlanning/tables/osadContextMenuActions";
import {
  useActionsMenuContext,
  type SummaryContext,
} from "../ActionsMenuContext";
import { NoResultFound } from "./SectionItem";

export const OSAD = () => {
  const theme = useTheme();
  const contextMenuRef = useRef<ContextMenuRef<SummaryContext>>(null);

  const { clearSelection } = useSelectionContext();
  const { openDialog } = useActionsMenuContext();
  const [serviceCenter] = useSelectedServiceCenter();
  const [selectedDate] = useDateSearchParamOrFallbackToToday();
  const planDate = dateToInt(selectedDate);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDraggedOverHeader, setIsDraggedOverHeader] = useState(false);

  const { data: osadShipments = osadDefaultValue, isLoading } =
    useOSADShipments(serviceCenter.id, true, selectedDate);

  const contextActions = useCallback(
    (context?: SummaryContext): ContextMenuAction[] =>
      osadContextMenuActions({
        serviceCenter,
        date: planDate,
        osad: osadShipments,
        shipmentContext: context ? [context] : [],
        openDialog,
        clearSelection,
      }),
    [clearSelection, openDialog, osadShipments, planDate, serviceCenter]
  );

  const isDroppedOnHeader: SxProps = {
    backgroundColor: theme.palette.action.hover,
    borderRadius: "0.5rem",
  };

  if (isLoading) return <Loading />;

  return (
    <>
      <DroppableHeader setIsDraggedOver={setIsDraggedOverHeader}>
        <CollapsibleItem
          title="OSAD"
          style={isDraggedOverHeader ? { ...isDroppedOnHeader } : null}
          displayCount={osadShipments.summary.bills}
          icon={<SyncProblem />}
          isExpanded={isExpanded}
          onExpand={(value: boolean) => setIsExpanded(value)}
        >
          <>
            {osadShipments.shipments.map((shipment, index) => (
              <Fragment key={shipment.shipmentId}>
                <OSADShipmentItem
                  shipment={shipment}
                  index={index}
                  contextRef={contextMenuRef}
                />
              </Fragment>
            ))}
            {osadShipments.shipments.length === 0 && (
              <NoResultFound>
                <ContentPaste sx={{ fontSize: "2rem" }} />
                <Typography>No OSAD shipments found.</Typography>
              </NoResultFound>
            )}
          </>
        </CollapsibleItem>
      </DroppableHeader>
      <ContextMenuDropdown actions={contextActions} ref={contextMenuRef} />
    </>
  );
};

interface DroppableHeaderProps {
  setIsDraggedOver: Dispatch<React.SetStateAction<boolean>>;
}

const DroppableHeader = ({
  children,
  setIsDraggedOver,
}: PropsWithChildren<DroppableHeaderProps>) => {
  const sectionRef = useRef(null);

  const dragData: DraggedItem = useMemo(
    () => ({
      id: "osad",
      source: "sidebar",
      type: "osad",
      parentId: "-1",
    }),
    []
  );

  useEffect(() => {
    const sectionEl = sectionRef.current;
    invariant(sectionEl, "Section component should exist");
    return dropTargetForElements({
      element: sectionEl,
      onDragEnter: () => setIsDraggedOver(true),
      onDragLeave: () => setIsDraggedOver(false),
      onDrop: () => setIsDraggedOver(false),
      getData: () => dragData,
    });
  }, [dragData, setIsDraggedOver]);

  return <Box ref={sectionRef}>{children}</Box>;
};
